<template>
  <main class="single-film-prototype single-person-prototype single-film-list-prototype single-person-list-prototype">
    <section class="hero g-0 position-relative">
      <HeroTopPanel
        :logo-src="logoPath"
        :background-src="defaultBackgroundImage"
        background-wrap-class="film-list-hero__bgWrap"
        :login-path="loginPath"
        @open-mobile-search="openMobileSearchOverlay"
      >
        <div class="row g-0 hero__grid align-items-end film-list-hero__grid">
          <div class="col hero__meta film-list-hero__meta">
            <div class="upper-container">
              <div class="user-event-container film-list-hero__meta-stack">
                <input
                  v-if="listEditMode"
                  v-model="editableListTitle"
                  class="item-title film-list-detail__title-input"
                  data-testid="person-list-title-input"
                  type="text"
                >
                <h1 v-else class="item-title" data-testid="person-list-title">
                  {{ personList.title || '' }}
                </h1>
                <p
                  v-if="personListLoaded && !personListError"
                  class="film-list-hero__meta-line"
                  data-testid="person-list-meta"
                >
                  {{ personCountLabel }}
                  <span aria-hidden="true"> • </span>
                  <CuratedListLabel v-if="personList.is_curated" tag="span" />
                  <template v-else>
                    <span>List by </span>
                    <a data-testid="person-list-owner-link" :href="personListOwnerPath">{{ personList.owner_name || 'Unknown' }}</a>
                  </template>
                  <span aria-hidden="true" class="film-list-detail__meta-separator--updated"> • </span>
                  <span class="film-list-detail__meta-updated">Updated {{ updatedAtLabel }}</span>
                </p>
                <div v-if="listFavouritesEnabled && !listEditMode" class="buttons film-list-hero__buttons">
                  <template v-if="!isAuthenticated">
                    <a class="btn single-btn person-fav-btn" data-testid="person-list-favourite-action" :href="loginPath">
                      <div class="default-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                    </a>
                  </template>
                  <template v-else>
                    <button
                      class="btn single-btn person-fav-btn film-list-hero__favourite-button"
                      data-testid="person-list-favourite-action"
                      type="button"
                      title="List favourites are not available yet."
                    >
                      <div class="default-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeroTopPanel>
    </section>

    <section class="mobile g-0 film-list-mobile-meta container">
      <div class="row g-0 item-title-mobile">
        <input
          v-if="listEditMode"
          v-model="editableListTitle"
          class="film-list-detail__title-input film-list-detail__title-input--mobile"
          data-testid="person-list-title-input-mobile"
          type="text"
        >
        <h1 v-else data-testid="person-list-title-mobile">{{ personList.title || '' }}</h1>
      </div>
      <div v-if="personListLoaded && !personListError" class="row g-0">
        <p class="film-list-hero__meta-line film-list-hero__meta-line--mobile">
          {{ personCountLabel }}
          <span aria-hidden="true"> • </span>
          <CuratedListLabel v-if="personList.is_curated" tag="span" />
          <template v-else>
            <span>List by </span>
            <a data-testid="person-list-owner-link-mobile" :href="personListOwnerPath">{{ personList.owner_name || 'Unknown' }}</a>
          </template>
          <span aria-hidden="true" class="film-list-detail__meta-separator--updated"> • </span>
          <span class="film-list-detail__meta-updated">Updated {{ updatedAtLabel }}</span>
        </p>
      </div>
      <div class="row g-0">
        <div class="col g-0">
          <div v-if="listFavouritesEnabled && !listEditMode" class="buttons">
            <template v-if="!isAuthenticated">
              <a class="btn single-btn" data-testid="person-list-favourite-action-mobile" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </a>
            </template>
            <template v-else>
              <button
                class="btn single-btn film-list-hero__favourite-button"
                data-testid="person-list-favourite-action-mobile"
                type="button"
                title="List favourites are not available yet."
              >
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </button>
            </template>
          </div>
        </div>
      </div>
    </section>

    <div class="container g-0 mobile-nav">
      <div class="row">
        <div class="col">
          <nav class="nav">
            <ul class="film-nav">
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'overview' }"
                  :to="overviewPath"
                  data-testid="person-list-overview-tab"
                >
                  Overview
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'comments' }"
                  :to="commentsPath"
                  data-testid="person-list-comments-tab"
                >
                  Comments
                </RouterLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div v-if="activeSection === 'overview'" class="container g-0">
      <div class="row g-0 film-overview">
        <div :class="overviewContentClass">
          <div class="container g-0">
            <div class="row g-0">
              <div class="col g-0">
                <div class="container g-0">
                  <div class="row g-0">
                    <div
                      v-if="listEditMode || personList.description || personListLoading || personListError"
                      class="col g-0 bio"
                    >
                      <template v-if="personListLoaded && !personListError && listEditMode">
                        <p
                          class="film-list-detail__description-label"
                          data-testid="person-list-description-label"
                        >
                          Overview
                        </p>
                        <textarea
                          v-model="editableListDescription"
                          class="film-list-page__description film-list-detail__description-textarea"
                          data-testid="person-list-description-textarea"
                          rows="5"
                        />
                      </template>
                      <p
                        v-else-if="personListLoaded && !personListError && personList.description"
                        class="film-list-page__description"
                        data-testid="person-list-description"
                      >
                        {{ personList.description }}
                      </p>
                      <p v-else-if="personListLoading" class="film-lists-empty" data-testid="person-list-loading">
                        Loading list...
                      </p>
                      <p v-else-if="personListError" class="film-lists-empty" data-testid="person-list-error">
                        {{ personListError }}
                      </p>
                    </div>
                  </div>
                </div>

                <template v-if="personListLoaded && !personListError">
                  <div
                    ref="listContentStartRef"
                    class="row g-0 sorting-buttons-row"
                    :class="{ 'sorting-buttons-row--list-editing': listEditMode }"
                  >
                    <div class="col g-0">
                      <div class="sorting-buttons">
                        <span class="sort-title">Sort:</span>
                        <div class="sorting-buttons__primary">
                          <a
                            v-for="button in primarySortButtons"
                            :key="button.key"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            href="#"
                            @click.prevent="setSortMode(button.key)"
                          >
                            {{ button.label }}
                          </a>
                        </div>
                        <div v-if="secondarySortButtons.length" class="sorting-buttons__secondary">
                          <a
                            v-for="button in secondarySortButtons"
                            :key="button.key"
                            :class="[
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            href="#"
                            @click.prevent="setSortMode(button.key)"
                          >
                            {{ button.label }}
                          </a>
                        </div>
                        <div class="sorting-buttons__desktop-view-row">
                          <a
                            v-for="button in viewModeButtons"
                            :key="`desktop-${button.key}`"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'view-mode-btn',
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            data-testid="person-list-desktop-view-mode-button"
                            href="#"
                            :aria-label="button.label"
                            :title="button.label"
                            @click.prevent="setViewMode(button.key)"
                          >
                            <ViewModeIcon :mode="button.key" data-testid="person-list-view-mode-icon" />
                          </a>
                        </div>
                        <div
                          v-if="hasFilterGroups"
                          class="modal"
                          data-testid="person-list-filter-modal"
                          :style="{ display: filtersModalOpen ? 'flex' : 'none' }"
                          @click.self="toggleFilters"
                        >
                          <button
                            class="person-compact-rating-picker__close person-filter-modal__close"
                            data-testid="person-list-filter-modal-close"
                            type="button"
                            aria-label="Close filters"
                            @click="toggleFilters"
                          >
                            <span class="person-compact-rating-picker__close-icon ion-close" aria-hidden="true"></span>
                          </button>
                          <div class="container person-filters">
                            <div class="person-filter-modal__header">
                              <h2>Filters:</h2>
                              <button
                                class="sorting-btn person-filter-modal__apply person-filter-modal__apply-mobile"
                                data-testid="person-list-filter-modal-apply"
                                type="button"
                                @click="toggleFilters"
                              >
                                Apply
                              </button>
                            </div>
                            <div
                              v-for="row in filterModalRows"
                              :key="row.key"
                              class="row g-0 filters-groups-row person-filter-modal__row"
                            >
                              <div
                                v-for="group in row.groups"
                                :key="group.key"
                                class="person-filter-modal__group person-filter-modal__group--half col-6"
                                data-testid="person-list-filter-modal-group"
                              >
                                <div class="single-filter-title-row">
                                  <h3>{{ group.title }}</h3>
                                </div>
                                <div class="filter-row">
                                  <a
                                    v-for="option in group.options"
                                    :key="option.value"
                                    :class="{ active: option.active }"
                                    href="#"
                                    @click.prevent="applyFilter(group.key, option.value)"
                                  >
                                    {{ option.label }}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="view-mode-btns">
                          <a
                            v-for="button in viewModeButtons"
                            :key="`mobile-${button.key}`"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'view-mode-btn',
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            href="#"
                            :aria-label="button.label"
                            :title="button.label"
                            @click.prevent="setViewMode(button.key)"
                          >
                            <ViewModeIcon :mode="button.key" />
                          </a>
                          <button
                            v-if="hasFilterGroups"
                            class="sorting-btn filter-btn view-mode-filter-btn"
                            data-testid="person-list-mobile-filter-button"
                            type="button"
                            @click="toggleFilters"
                          >
                            {{ filterButtonLabel }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="!reorderMode" class="row g-0 filters-section-sm">
                    <div class="row g-0">
                      <div class="col g-0">
                        <div class="container g-0">
                          <div class="row g-0">
                            <div class="col-9 g-0 filtering-row">
                              <input
                                v-model="titleFilter"
                                class="filmography-filter"
                                data-testid="person-list-filter-input"
                                type="search"
                                placeholder="Filter by name..."
                              >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="personListCanEdit"
                    class="row g-0 list-addition-row film-list-detail__edit-row"
                  >
                    <div class="col g-0">
                      <button
                        v-if="!reorderMode"
                        class="btn-sm list-addition-btn"
                        data-testid="person-list-edit-button"
                        type="button"
                        :disabled="resetNumbersPending"
                        @click="startListEditing"
                      >
                        {{ listEditMode ? 'Stop editing' : 'Edit list' }}
                      </button>
                      <label
                        v-if="listEditMode"
                        class="film-list-detail__public-edit"
                        data-testid="person-list-public-edit-field"
                      >
                        <input
                          v-model="editableListPublic"
                          data-testid="person-list-public-edit"
                          type="checkbox"
                        >
                        <span>Public</span>
                      </label>
                      <button
                        v-if="!listEditMode && !reorderMode"
                        class="btn-sm"
                        data-testid="person-list-reorder-button"
                        type="button"
                        :disabled="resetNumbersPending"
                        @click="startReordering"
                      >
                        Reorder items
                      </button>
                      <ResetListNumbers
                        v-if="!listEditMode && !reorderMode"
                        kind="person"
                        :pending="resetNumbersPending"
                        :notice="resetNumbersNotice"
                        @reset="resetNumbers"
                      />
                      <button
                        v-if="reorderMode"
                        class="btn-sm"
                        data-testid="person-list-reorder-done-button"
                        type="button"
                        @click="finishReordering"
                      >
                        Done reordering
                      </button>
                      <button
                        v-if="reorderMode"
                        class="btn-sm"
                        data-testid="person-list-reorder-save-button"
                        type="button"
                        @click="saveReordering"
                      >
                        Save new order
                      </button>
                      <button
                        v-if="reorderMode && hasReorderChanges"
                        class="btn-sm film-list-detail__discard-button"
                        data-testid="person-list-reorder-discard-button"
                        type="button"
                        @click="discardReorderChanges"
                      >
                        Discard changes
                      </button>
                      <p
                        v-if="reorderMode"
                        class="film-list-detail__reorder-instructions"
                        data-testid="person-list-reorder-instructions"
                      >
                        Use the arrow buttons to move items up or down.
                      </p>
                      <div v-if="listEditMode" class="film-list-detail__edit-actions">
                        <button
                          class="btn-sm"
                          data-testid="person-list-save-button"
                          type="button"
                          @click="saveListEditing"
                        >
                          Save
                        </button>
                        <button
                          v-if="hasUnsavedChanges"
                          class="btn-sm film-list-detail__discard-button"
                          data-testid="person-list-discard-button"
                          type="button"
                          @click="discardListEditing"
                        >
                          Discard changes
                        </button>
                      </div>
                      <p
                        v-if="showUnsavedChangesNotice"
                        class="film-list-detail__edit-warning"
                        data-testid="person-list-unsaved-notice"
                      >
                        You have unsaved changes. Save or discard the changes.
                      </p>
                      <p
                        v-if="showSavedChangesNotice"
                        class="film-list-detail__edit-success"
                        data-testid="person-list-saved-notice"
                        @click="dismissSavedChangesNotice"
                      >
                        Changes saved.
                      </p>
                      <div v-if="listEditMode && showAddItemSearch" class="film-list-detail__add-item-panel">
                        <p class="film-list-detail__add-item-title" data-testid="person-list-add-item-title">
                          Add new list item
                        </p>
                        <div class="top-search-autocomplete film-list-detail__add-item-search">
                          <div class="top-search-autocomplete__field">
                            <input
                              v-model="addItemQuery"
                              class="top-search form-control"
                              data-testid="person-list-add-item-input"
                              type="search"
                              placeholder="Search people..."
                              @input="handleAddItemSearchInput"
                              @keydown.down.prevent="highlightNextAddItemResult"
                              @keydown.up.prevent="highlightPreviousAddItemResult"
                              @keydown.enter.prevent="handleAddItemEnter"
                              @keydown.esc="resetAddItemSearch"
                            >
                            <div
                              v-if="addItemSearchResults.length > 0"
                              class="top-search-autocomplete__results"
                              data-testid="person-list-add-item-results"
                            >
                              <button
                                v-for="(result, index) in addItemSearchResults"
                                :key="`person-list-add-item-${result.id}`"
                                :ref="(element) => setAddItemResultRef(element, index)"
                                :class="[
                                  'top-search-autocomplete__result',
                                  'film-list-detail__add-item-result',
                                  {
                                    'top-search-autocomplete__result--active': index === addItemActiveIndex,
                                    'top-search-autocomplete__result--disabled': isPersonAlreadyOnList(result.id),
                                  },
                                ]"
                                :disabled="isPersonAlreadyOnList(result.id)"
                                data-testid="person-list-add-item-result"
                                type="button"
                                @mouseenter="setSelectableAddItemActiveIndex(result, index)"
                                @focus="setSelectableAddItemActiveIndex(result, index)"
                                @click="addSearchResult(result)"
                              >
                                <span class="top-search-autocomplete__label">
                                  <span class="top-search-autocomplete__keyword">{{ result.name }}</span>
                                  <span
                                    v-if="isPersonAlreadyOnList(result.id)"
                                    class="top-search-autocomplete__year"
                                  >
                                    (already on the list)
                                  </span>
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                        <p
                          v-if="addItemNotice"
                          class="film-list-detail__edit-success"
                          data-testid="person-list-add-item-notice"
                          @click="addItemNotice = ''"
                        >
                          {{ addItemNotice }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div class="container g-0">
                    <div class="row g-0">
                      <div class="col g-0">
                        <div
                          v-if="reorderMode"
                          class="container g-0 person-text-only-credits-list film-list-detail__text-list film-list-detail__reorder-list"
                          data-testid="person-list-reorder-list"
                        >
                          <p
                            v-if="reorderMoveNotice.position === 'above'"
                            class="film-list-detail__reorder-move-notice film-list-detail__reorder-move-notice--above"
                            data-testid="person-list-reorder-notice-above"
                          >
                            {{ reorderMoveNotice.text }}
                          </p>
                          <p
                            v-if="reorderPaginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="person-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <div
                            v-for="(item, index) in reorderPaginatedItems"
                            v-else
                            :key="item.id"
                            :class="[
                              'row',
                              'g-0',
                              'person-text-only-credit-row',
                              'film-list-detail__text-row',
                              index === reorderPaginatedItems.length - 1 ? 'person-text-only-credit-row--last' : null,
                            ]"
                            data-testid="person-list-reorder-row"
                          >
                            <div class="col g-0 person-text-only-credit-container">
                              <div class="person-text-only-row person-list-detail__text-row film-list-detail__text-row-top">
                                <span
                                  class="film-list-detail__text-number film-list-detail__reorder-number"
                                  data-testid="person-list-reorder-number"
                                >
                                  {{ reorderDisplayNumber(index) }}
                                </span>
                                <div class="film-list-detail__reorder-position-actions">
                                  <span
                                    v-if="isFirstReorderItem(item.id)"
                                    class="film-list-detail__reorder-position-placeholder"
                                    aria-hidden="true"
                                  ></span>
                                  <button
                                    v-else
                                    class="btn-sm film-list-detail__reorder-position-button"
                                    data-testid="person-list-reorder-move-up"
                                    type="button"
                                    title="Move item up"
                                    aria-label="Move item up"
                                    @click="moveReorderItem(item.id, -1)"
                                  >
                                    ↑
                                  </button>
                                  <button
                                    v-if="!isLastReorderItem(item.id)"
                                    class="btn-sm film-list-detail__reorder-position-button"
                                    data-testid="person-list-reorder-move-down"
                                    type="button"
                                    title="Move item down"
                                    aria-label="Move item down"
                                    @click="moveReorderItem(item.id, 1)"
                                  >
                                    ↓
                                  </button>
                                </div>
                                <a
                                  :href="item.person.url || '#'"
                                  class="film-list-detail__text-title-link"
                                  data-testid="person-list-text-title-link"
                                >
                                  <h3 class="person-text-only-credit-film-title person-list-detail__text-name">
                                    {{ truncatedPersonName(item.person.name) }}
                                  </h3>
                                </a>
                              </div>
                            </div>
                          </div>
                          <p
                            v-if="reorderMoveNotice.position === 'below'"
                            class="film-list-detail__reorder-move-notice film-list-detail__reorder-move-notice--below"
                            data-testid="person-list-reorder-notice-below"
                          >
                            {{ reorderMoveNotice.text }}
                          </p>
                        </div>

                        <div
                          v-else-if="currentViewMode === '1'"
                          class="container g-0 credits-list film-list-detail__credits-list"
                          data-testid="person-list-full-view"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="person-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <template v-for="item in paginatedItems" :key="item.id">
                            <div
                              v-if="listEditMode && isListItemPendingRemoval(item.id)"
                              class="row g-0 film-list-detail__removed-placeholder-wrap"
                              data-testid="person-list-item-removed-placeholder-wrap"
                            >
                              <div class="col g-0">
                                <div class="film-list-detail__removed-placeholder" data-testid="person-list-item-removed-placeholder">
                                  <span class="film-list-detail__removed-placeholder-text">
                                    '{{ item.person.name }}' is to be deleted on save.
                                  </span>
                                  <button
                                    class="btn-sm film-list-detail__discard-button film-list-detail__item-remove-button"
                                    data-testid="person-list-item-undo-remove-button"
                                    type="button"
                                    @click="undoListItemRemoval(item.id)"
                                  >
                                    Undo
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="!(listEditMode && isListItemPendingRemoval(item.id))"
                              class="row g-0 single-credit-row film-list-detail__row person-list-detail__row"
                              data-testid="person-list-item-row"
                            >
                              <div class="col-3 g-0 film-poster-col film-list-detail__poster-col">
                                <p class="film-list-detail__number" data-testid="person-list-item-number">
                                  {{ item.number || '–' }}
                                </p>
                                <div class="film-poster-container">
                                  <a :href="item.person.url || '#'">
                                    <img
                                      class="film-poster person-list-detail__portrait-image"
                                      :src="item.person.poster_url || portraitPlaceholderImage"
                                      :alt="`${item.person.name} portrait`"
                                    >
                                  </a>
                                </div>
                              </div>
                              <div class="col g-0">
                                <div class="container g-0 single-credit-container film-list-detail__container person-list-detail__container">
                                  <div class="row g-0 credit-title-row person-list-detail__title-row">
                                    <div class="col g-0">
                                      <a :href="item.person.url || '#'">
                                        <h3 class="credit-film-title person-list-detail__name">
                                          {{ truncatedPersonName(item.person.name) }}
                                        </h3>
                                      </a>
                                    </div>
                                  </div>
                                  <div v-if="!listEditMode" class="row g-0 person-list-detail__button-row">
                                    <div class="col g-0">
                                      <template v-if="isAuthenticated">
                                        <button
                                          class="btn single-btn person-list-detail__favourite-button"
                                          :class="{ 'btn--favourited': item.person.favourited }"
                                          data-testid="person-list-item-favourite-action"
                                          type="button"
                                          :disabled="personFavouritePendingById[item.person.person_id]"
                                          @click="toggleListItemFavourite(item)"
                                        >
                                          <div class="default-btn">
                                            <i class="ion-heart"></i>
                                            <span class="fav-btn">{{ listItemFavouriteButtonText(item) }}</span>
                                            <span class="fav-btn-sm">{{ listItemFavouriteButtonShortText(item) }}</span>
                                          </div>
                                        </button>
                                      </template>
                                      <template v-else>
                                        <a
                                          class="btn single-btn person-list-detail__favourite-button"
                                          data-testid="person-list-item-favourite-action"
                                          :href="loginPath"
                                        >
                                          <div class="default-btn">
                                            <i class="ion-heart"></i>
                                            <span class="fav-btn">Add to favourites</span>
                                            <span class="fav-btn-sm">Favourite</span>
                                          </div>
                                        </a>
                                      </template>
                                    </div>
                                  </div>
                                  <div
                                    v-if="listEditMode || item.description"
                                    :class="[
                                      'row',
                                      'g-0',
                                      'film-list-detail__description-row',
                                      'person-list-detail__description-row',
                                      listEditMode ? 'film-list-detail__description-row--editing' : null,
                                    ]"
                                  >
                                    <div class="col g-0">
                                      <textarea
                                        v-if="listEditMode"
                                        v-model="editableItemDescriptions[item.id]"
                                        class="film-list-detail__description-textarea film-list-detail__item-description-textarea"
                                        data-testid="person-list-item-description-textarea"
                                        rows="5"
                                      />
                                      <p v-else class="film-list-detail__description-text">{{ item.description }}</p>
                                      <div
                                        v-if="listEditMode"
                                        class="film-list-detail__item-remove-row"
                                      >
                                        <button
                                          class="btn-sm film-list-detail__discard-button film-list-detail__item-remove-button"
                                          data-testid="person-list-item-remove-button"
                                          type="button"
                                          @click="removeListItemFromEditing(item.id)"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </template>
                        </div>

                        <div
                          v-else-if="currentViewMode === '2'"
                          class="person-posters-grid film-list-detail__posters-grid"
                          data-testid="person-list-posters-grid"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="person-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <div
                            v-for="item in paginatedItems"
                            v-else
                            :key="item.id"
                            class="film-list-detail__poster-card"
                            data-testid="person-list-poster-card"
                          >
                            <div class="film-list-detail__poster-media">
                              <p class="film-list-detail__poster-number">{{ item.number || '–' }}</p>
                              <a class="person-poster-link" :href="item.person.url || '#'">
                                <img
                                  class="person-poster-grid-image person-list-detail__poster-grid-image"
                                  :src="item.person.poster_url || portraitPlaceholderImage"
                                  :alt="`${item.person.name} portrait`"
                                >
                              </a>
                            </div>
                          </div>
                        </div>

                        <div
                          v-else
                          class="container g-0 person-text-only-credits-list film-list-detail__text-list"
                          data-testid="person-list-text-only-list"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="person-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <div
                            v-for="(item, index) in paginatedItems"
                            v-else
                            :key="item.id"
                            :class="[
                              'row',
                              'g-0',
                              'person-text-only-credit-row',
                              index === paginatedItems.length - 1 ? 'person-text-only-credit-row--last' : null,
                            ]"
                            data-testid="person-list-text-row"
                          >
                            <div class="col g-0 person-text-only-credit-container">
                              <div class="person-text-only-row person-list-detail__text-row">
                                <span class="film-list-detail__text-number">{{ item.number || '–' }}</span>
                                <a
                                  :href="item.person.url || '#'"
                                  class="film-list-detail__text-title-link"
                                  data-testid="person-list-text-title-link"
                                >
                                  <h3 class="person-text-only-credit-film-title person-list-detail__text-name">
                                    {{ truncatedPersonName(item.person.name) }}
                                  </h3>
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>

                        <nav
                          v-if="effectiveNumPages > 1"
                          class="film-lists-pagination film-list-detail__pagination film-list-detail__pagination--bootstrap film-list-detail__pagination--with-mobile"
                          aria-label="Person list pagination"
                        >
                          <ul class="pagination mb-0">
                            <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                              <button class="page-link" data-testid="person-list-prev" type="button" @click="goToPage(currentPage - 1)">Previous</button>
                            </li>
                            <li v-for="pageItem in paginationItems" :key="`person-list-bottom-${pageItem.key}`" class="page-item" :class="{ active: pageItem.page === currentPage, disabled: pageItem.ellipsis }">
                              <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">{{ pageItem.label }}</span>
                              <button v-else class="page-link" :data-testid="`person-list-page-${pageItem.page}`" type="button" :aria-label="`Go to page ${pageItem.page}`" @click="goToPage(pageItem.page)">{{ pageItem.label }}</button>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage >= effectiveNumPages }">
                              <button class="page-link" data-testid="person-list-next" type="button" @click="goToPage(currentPage + 1)">Next</button>
                            </li>
                          </ul>
                          <CompactMobilePagination
                            :current-page="currentPage"
                            :total-pages="effectiveNumPages"
                            test-id-prefix="person-list"
                            @page-change="goToPage"
                          />
                        </nav>

                        <div
                          v-if="listEditMode && personListCanEdit"
                          class="film-list-detail__delete-row"
                        >
                          <button
                            class="btn-sm film-list-detail__delete-button"
                            data-testid="person-list-delete-button"
                            type="button"
                            :disabled="listDeletePending"
                            @click="deletePersonList"
                          >
                            Delete list
                          </button>
                        </div>
                      </div>

                      <div
                        v-if="hasFilterGroups"
                        class="col-3 g-0 filters-section"
                        data-testid="person-list-filters-sidebar"
                      >
                        <div class="container person-filters">
                          <div class="row g-0 filters-title-row">
                            <div class="col g-0">
                              <h3>Filters:</h3>
                            </div>
                          </div>

                          <template v-for="group in filterGroups" :key="`sidebar-${group.key}`">
                            <div class="row g-0 single-filter-title-row">
                              <div class="col g-0">
                                <h3>{{ group.title }}</h3>
                              </div>
                            </div>
                            <div class="row g-0 filter-row">
                              <div class="col g-0">
                                <a
                                  v-for="option in group.options"
                                  :key="option.value"
                                  :class="{ active: option.active }"
                                  href="#"
                                  @click.prevent="applyFilter(group.key, option.value)"
                                >
                                  {{ option.label }}
                                </a>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <div v-else class="container g-0" data-testid="comments-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <CommentsPanel
            :base-path="overviewPath"
            heading="Discussion"
            :is-active="activeSection === 'comments'"
            target-type="person-list"
            :target-id="listId"
            test-id-prefix="person-list"
          />
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
  </main>
</template>

<script setup>
import CuratedListLabel from '../components/CuratedListLabel.vue'
import ResetListNumbers from '../components/ResetListNumbers.vue'
import { useListNumberReset } from '../utils/listNumberReset'
import { usePageViewTracking } from '../analytics/pageViews'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CommentsPanel from '../components/CommentsPanel.vue'
import CompactMobilePagination from '../components/CompactMobilePagination.vue'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import ViewModeIcon from '../components/ViewModeIcon'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/single-person/person-detail.css'
import '../assets/single-film-list/styles.css'
import '../assets/single-person-list/styles.css'
import { useAuthStore } from '../stores/auth'
import { buildLoginPath } from '../utils/auth'
import { compactPaginationItems } from '../utils/pagination'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { buildMetaDescription, useMetaDescription } from '../utils/metaDescription'

const route = useRoute()
const capturePageView = usePageViewTracking()
const router = useRouter()
const auth = useAuthStore()
auth.initFromStorage()

const logoPath = '/static/spa/branding/logo.png'
const defaultBackgroundImage = '/media/films/backgrounds/default-bg.jpg'
const portraitPlaceholderImage = '/static/films/img/placeholder-person.svg'
const pageSize = 50
const listFavouritesEnabled = false
const showAddItemSearch = false
const personNameDisplayLimit = 80

const mobileSearchOpen = ref(false)
const filtersModalOpen = ref(false)
const personListLoading = ref(false)
const personListLoaded = ref(false)
const personListError = ref('')
const personList = ref(emptyPersonList())
const documentTitle = computed(() => buildDocumentTitle([personList.value.title, 'Person list']))
const selectedActivity = ref('all')
const sortMode = ref('number-asc')
const currentViewMode = ref('1')
const viewModeBeforeReordering = ref('1')
const titleFilter = ref('')
const currentPage = ref(1)
const listContentStartRef = ref(null)
const ignoreFilterPageReset = ref(false)
const personFavouritePendingById = ref({})
const listEditMode = ref(false)
const reorderMode = ref(false)
const editableListTitle = ref('')
const editableListDescription = ref('')
const editableListPublic = ref(true)
const editableItemDescriptions = ref({})
const savedEditableItems = ref([])
const pendingAddedItems = ref([])
const removedItemIds = ref([])
const addItemQuery = ref('')
const addItemSearchResults = ref([])
const addItemActiveIndex = ref(-1)
const addItemNotice = ref('')
const reorderedItemIds = ref([])
const savedReorderItemIds = ref([])
const reorderItemNumbers = ref({})
const savedReorderItemNumbers = ref({})
const reorderPageLengths = ref([])
const savedReorderPageLengths = ref([])
const reorderMoveNotice = ref({ text: '', position: '' })
const showUnsavedChangesNotice = ref(false)
const showSavedChangesNotice = ref(false)
const listDeletePending = ref(false)
let savedChangesNoticeTimeoutId = null
let reorderMoveNoticeTimeoutId = null
let addItemSearchRequestId = 0
const addItemResultRefs = []
const deleteListConfirmationMessage = "Delete this list? This will permanently delete the whole list and all its list items. This deletion can't be reverted."
const listDeletedNoticeStorageKey = 'filmsomniac:listDeletedNotice'

const authUsername = computed(() => auth.state.username || 'Account')
const isAuthenticated = computed(() => auth.isAuthenticated.value)
const listSlugParam = computed(() => String(route.params.listSlug || ''))
const listId = computed(() => {
  const match = listSlugParam.value.match(/^(\d+)/)
  return match ? Number.parseInt(match[1], 10) : null
})
const activeSection = computed(() => (
  route.params.section === 'comments' ? 'comments' : 'overview'
))
const slugifiedTitle = computed(() => slugifyText(personList.value.title || '') || 'list')
const overviewPath = computed(() => (
  listId.value ? `/lists/person/${listId.value}-${slugifiedTitle.value}` : '/lists/person'
))
const commentsPath = computed(() => `${overviewPath.value}/comments`)
const loginPath = computed(() => buildLoginPath(route.fullPath || overviewPath.value))
const allItems = computed(() => Array.isArray(personList.value.items) ? personList.value.items : [])
const personListOwnerPath = computed(() => buildFilmsomniacPath(personList.value.owner_name))

const personCountLabel = computed(() => {
  const count = personList.value.person_count || 0
  return `${count} ${count === 1 ? 'person' : 'people'}`
})

const updatedAtLabel = computed(() => formatShortDate(personList.value.updated_at))

const hasUnsavedChanges = computed(() => (
  editableListTitle.value !== (personList.value.title || '')
    || editableListDescription.value !== (personList.value.description || '')
    || editableListPublic.value !== Boolean(personList.value.public)
    || !editableItemsMatchSavedItems(personList.value.items, savedEditableItems.value)
    || removedItemIds.value.length > 0
))

const hasPendingItemDeletions = computed(() => removedItemIds.value.length > 0)

const sortButtons = computed(() => [
  { key: 'number-asc', label: 'Number 0-9', first: true, active: sortMode.value === 'number-asc' },
  { key: 'number-desc', label: 'Number 9-0', active: sortMode.value === 'number-desc' },
  { key: 'name-asc', label: 'Name A-Z', active: sortMode.value === 'name-asc' },
  { key: 'name-desc', label: 'Name Z-A', active: sortMode.value === 'name-desc' },
])

const primarySortButtons = computed(() => sortButtons.value.slice(0, 2))
const secondarySortButtons = computed(() => sortButtons.value.slice(2))

const viewModeButtons = computed(() => [
  {
    key: '1',
    label: 'Detailed view',
    first: true,
    active: currentViewMode.value === '1',
  },
  { key: '2', label: 'Poster grid view', active: currentViewMode.value === '2' },
  { key: '4', label: 'Text list view', active: currentViewMode.value === '4' },
])

function serverFilterCounts(groupKey) {
  if (!hasServerPagination.value) {
    return null
  }
  const counts = personList.value.filter_counts?.[groupKey]
  return counts && typeof counts === 'object' ? counts : null
}

function filterCount(counts, key, fallback) {
  const value = Number.parseInt(String(counts?.[key] ?? ''), 10)
  return Number.isFinite(value) ? value : fallback
}

const activityCounts = computed(() => ({
  all: filterCount(serverFilterCounts('activity'), 'all', allItems.value.length),
  favourited: filterCount(
    serverFilterCounts('activity'),
    'favourited',
    countMatches(allItems.value, (item) => Boolean(item.person.favourited)),
  ),
}))

const activityFilterOptions = computed(() => {
  if (!isAuthenticated.value) {
    return []
  }

  return [
    {
      value: 'all',
      label: `All (${activityCounts.value.all})`,
      active: selectedActivity.value === 'all',
    },
    {
      value: 'favourited',
      label: `Favourited (${activityCounts.value.favourited})`,
      active: selectedActivity.value === 'favourited',
    },
  ]
})

const activityScopedItems = computed(() => {
  if (hasServerPagination.value) {
    return allItems.value
  }
  if (!isAuthenticated.value || selectedActivity.value === 'all') {
    return allItems.value
  }

  return allItems.value.filter((item) => Boolean(item.person.favourited))
})

const normalizedTitleFilter = computed(() => titleFilter.value.trim().toLowerCase())

const filteredItems = computed(() => {
  if (!normalizedTitleFilter.value) {
    return activityScopedItems.value
  }

  return activityScopedItems.value.filter((item) => (
    String(item.person.name || '').toLowerCase().includes(normalizedTitleFilter.value)
  ))
})

const sortedItems = computed(() => {
  const items = [...filteredItems.value]

  switch (sortMode.value) {
    case 'number-desc':
      return items.sort((left, right) => numericCompare(right.number, left.number, right.person.name, left.person.name))
    case 'name-asc':
      return items.sort((left, right) => compareStrings(left.person.name, right.person.name))
    case 'name-desc':
      return items.sort((left, right) => compareStrings(right.person.name, left.person.name))
    case 'number-asc':
    default:
      return items.sort((left, right) => numericCompare(left.number, right.number, left.person.name, right.person.name))
  }
})

const hasServerPagination = computed(() => (
  personList.value.server_paginated
    && Number.parseInt(personList.value.page_size, 10) === pageSize
    && Number.parseInt(personList.value.num_pages, 10) > 0
))

const numPages = computed(() => {
  if (hasServerPagination.value) {
    return Math.max(1, Number.parseInt(personList.value.num_pages, 10) || 1)
  }
  return Math.max(1, Math.ceil(sortedItems.value.length / pageSize))
})

const paginatedItems = computed(() => {
  if (hasServerPagination.value) {
    return sortedItems.value
  }
  const startIndex = (currentPage.value - 1) * pageSize
  return sortedItems.value.slice(startIndex, startIndex + pageSize)
})

const reorderOrderedItems = computed(() => {
  if (!reorderMode.value) {
    return sortedItems.value
  }

  const itemsById = new Map(sortedItems.value.map((item) => [item.id, item]))
  const orderedIds = reorderedItemIds.value.filter((id) => itemsById.has(id))
  const missingIds = sortedItems.value
    .map((item) => item.id)
    .filter((id) => !orderedIds.includes(id))

  return [...orderedIds, ...missingIds]
    .map((id) => itemsById.get(id))
    .filter(Boolean)
})

const reorderNumPages = computed(() => Math.max(1, reorderPageLengths.value.length || 1))

const effectiveNumPages = computed(() => (
  reorderMode.value ? reorderNumPages.value : numPages.value
))
const paginationItems = computed(() => compactPaginationItems(currentPage.value, effectiveNumPages.value))

const reorderPaginatedItems = computed(() => {
  const startIndex = reorderPageStartIndex(currentPage.value)
  const currentLength = reorderPageLengths.value[currentPage.value - 1] || pageSize
  return reorderOrderedItems.value.slice(startIndex, startIndex + currentLength)
})

const hasReorderChanges = computed(() => {
  if (!reorderMode.value) {
    return false
  }

  if (reorderedItemIds.value.length !== savedReorderItemIds.value.length) {
    return true
  }

  if (reorderPageLengths.value.length !== savedReorderPageLengths.value.length) {
    return true
  }

  if (reorderPageLengths.value.some((length, index) => length !== savedReorderPageLengths.value[index])) {
    return true
  }

  if (reorderedItemIds.value.some((id, index) => id !== savedReorderItemIds.value[index])) {
    return true
  }

  return reorderedItemIds.value.some((id) => (
    reorderItemNumbers.value[id] !== savedReorderItemNumbers.value[id]
  ))
})

const noItemsMessage = computed(() => (
  activityScopedItems.value.length === 0
    ? 'No people on this list.'
    : 'No people found.'
))

const showNoResults = computed(() => (
  activityScopedItems.value.length > 0 && filteredItems.value.length === 0
))

const filterGroups = computed(() => (
  isAuthenticated.value
    ? [
      {
        key: 'activity',
        title: 'Your activity',
        options: activityFilterOptions.value,
      },
    ]
    : []
))

const hasFilterGroups = computed(() => filterGroups.value.length > 0)
const filterModalRows = computed(() => {
  const groups = filterGroups.value
  const rows = []

  for (let index = 0; index < groups.length; index += 2) {
    const rowGroups = groups.slice(index, index + 2)
    rows.push({
      key: rowGroups.map((group) => group.key).join('-'),
      groups: rowGroups,
    })
  }

  return rows
})
const filterButtonLabel = computed(() => {
  return 'Filters'
})

const overviewContentClass = computed(() => (
  hasFilterGroups.value
    ? 'col-12 col-sm-12 col-md-12 col-lg-9'
    : 'col-12 col-sm-12 col-md-12 col-lg-10'
))

function pageFromRoute() {
  const rawPage = Array.isArray(route.query.page) ? route.query.page[0] : route.query.page
  const page = Number.parseInt(String(rawPage || ''), 10)
  return Number.isFinite(page) && page > 0 ? page : 1
}

function normalizedPage(pageNumber) {
  return Math.min(Math.max(pageNumber, 1), effectiveNumPages.value)
}

function queryForPage(pageNumber) {
  const query = { ...route.query }
  if (pageNumber > 1) {
    query.page = String(pageNumber)
  } else {
    delete query.page
  }
  return query
}

function syncPageRoute(pageNumber, { replace = true } = {}) {
  const location = { path: route.path, query: queryForPage(pageNumber) }
  return replace ? router.replace(location) : router.push(location)
}

function resetCurrentPage() {
  currentPage.value = 1
  void syncPageRoute(1)
}

async function scrollToListContentStart() {
  await nextTick()
  if (typeof listContentStartRef.value?.scrollIntoView === 'function') {
    listContentStartRef.value.scrollIntoView({ behavior: 'auto', block: 'start' })
  }
}

watch(selectedActivity, () => {
  if (ignoreFilterPageReset.value) {
    return
  }
  if (!personListLoaded.value) {
    return
  }
  resetCurrentPage()
  if (hasServerPagination.value && !listEditMode.value && !reorderMode.value) {
    void loadPersonListDetail(listId.value, { page: 1, preserveFilters: true })
  }
})

watch([sortMode, titleFilter], () => {
  if (ignoreFilterPageReset.value) {
    return
  }
  if (!personListLoaded.value) {
    return
  }
  if (!hasServerPagination.value) {
    resetCurrentPage()
  }
})

watch(activityFilterOptions, (options) => {
  if (!isAuthenticated.value) {
    selectedActivity.value = 'all'
    return
  }
  if (!options.some((option) => option.value === selectedActivity.value)) {
    selectedActivity.value = 'all'
  }
}, { immediate: true })

watch([editableListTitle, editableListDescription, editableItemDescriptions, savedEditableItems, removedItemIds], () => {
  if (listEditMode.value && hasUnsavedChanges.value) {
    dismissSavedChangesNotice()
  }
}, { deep: true })

watch(effectiveNumPages, (value) => {
  if (currentPage.value > value) {
    currentPage.value = value
    void syncPageRoute(value)
  }
})

watch(() => route.query.page, async () => {
  currentPage.value = normalizedPage(pageFromRoute())
  if (
    listId.value
      && personListLoaded.value
      && hasServerPagination.value
      && !reorderMode.value
  ) {
    await loadPersonListDetail(listId.value, {
      preserveFilters: true,
      preserveEditMode: listEditMode.value,
    })
  }
})

watch([listId, isAuthenticated], async ([currentListId]) => {
  if (!currentListId) {
    personList.value = emptyPersonList()
    personListLoaded.value = true
    personListError.value = 'This list could not be found.'
    return
  }
  await loadPersonListDetail(currentListId)
}, { immediate: true })

watch(documentTitle, setDocumentTitle, { immediate: true })

onBeforeUnmount(() => {
  clearSavedChangesNoticeTimeout()
  clearReorderMoveNoticeTimeout()
})

function emptyPersonList() {
  return {
    id: null,
    title: '',
    description: '',
    owner_name: '',
    is_owner: false,
    public: true,
    person_count: 0,
    page: 1,
    page_size: pageSize,
    num_pages: 1,
    server_paginated: false,
    filter_counts: {},
    updated_at: null,
    items: [],
  }
}

function clearSavedChangesNoticeTimeout() {
  if (savedChangesNoticeTimeoutId !== null) {
    window.clearTimeout(savedChangesNoticeTimeoutId)
    savedChangesNoticeTimeoutId = null
  }
}

function dismissSavedChangesNotice() {
  clearSavedChangesNoticeTimeout()
  showSavedChangesNotice.value = false
}

function scheduleSavedChangesNoticeDismiss() {
  clearSavedChangesNoticeTimeout()
  savedChangesNoticeTimeoutId = window.setTimeout(() => {
    showSavedChangesNotice.value = false
    savedChangesNoticeTimeoutId = null
  }, 3000)
}

function clearReorderMoveNoticeTimeout() {
  if (reorderMoveNoticeTimeoutId !== null) {
    window.clearTimeout(reorderMoveNoticeTimeoutId)
    reorderMoveNoticeTimeoutId = null
  }
}

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

function buildAuthHeaders() {
  const headers = {}
  if (auth.state.access) {
    headers.Authorization = `Bearer ${auth.state.access}`
  }
  return headers
}

function buildJsonAuthHeaders() {
  return {
    ...buildAuthHeaders(),
    'Content-Type': 'application/json',
  }
}

function buildEditableItemDescriptions(items) {
  return Object.fromEntries(
    (Array.isArray(items) ? items : []).map((item) => [item.id, item.description || '']),
  )
}

function resetRemovedItemIds() {
  removedItemIds.value = []
}

function isListItemPendingRemoval(itemId) {
  return removedItemIds.value.includes(itemId)
}

function clonePersonListItems(items) {
  return (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    person: item?.person ? { ...item.person } : item.person,
  }))
}

function editableItemsMatchSavedItems(currentItems, savedItems) {
  if (currentItems.length !== savedItems.length) {
    return false
  }

  return currentItems.every((item, index) => {
    const savedItem = savedItems[index]
    return savedItem
      && item.id === savedItem.id
      && (editableItemDescriptions.value[item.id] ?? '') === (savedItem.description || '')
  })
}

function resetAddItemSearch() {
  addItemSearchRequestId += 1
  addItemQuery.value = ''
  addItemSearchResults.value = []
  addItemActiveIndex.value = -1
  addItemResultRefs.length = 0
}

function resetPendingAddedItems() {
  pendingAddedItems.value = []
}

function isPersonAlreadyOnList(personId) {
  return personList.value.items.some((item) => item?.person?.id === personId)
    || pendingAddedItems.value.some((item) => item?.person?.id === personId)
}

async function handleAddItemSearchInput() {
  if (!listEditMode.value || !personListCanEdit.value || !listId.value) {
    resetAddItemSearch()
    return
  }

  const query = addItemQuery.value.trim()
  const requestId = ++addItemSearchRequestId

  if (query.length < 2) {
    addItemSearchResults.value = []
    addItemActiveIndex.value = -1
    addItemResultRefs.length = 0
    return
  }

  try {
    const response = await fetch(`/api/v1/people/lists/${listId.value}/search?q=${encodeURIComponent(query)}`, {
      headers: buildAuthHeaders(),
    })

    if (requestId !== addItemSearchRequestId) {
      return
    }

    if (!response.ok) {
      addItemSearchResults.value = []
      return
    }

    const data = await response.json()
    addItemSearchResults.value = Array.isArray(data.results) ? data.results : []
    addItemActiveIndex.value = -1
    addItemResultRefs.length = 0
  } catch {
    if (requestId === addItemSearchRequestId) {
      addItemSearchResults.value = []
      addItemActiveIndex.value = -1
      addItemResultRefs.length = 0
    }
  }
}

function setAddItemResultRef(element, index) {
  if (!element) {
    return
  }
  addItemResultRefs[index] = element
}

async function scrollActiveAddItemResultIntoView() {
  await nextTick()
  const activeElement = addItemResultRefs[addItemActiveIndex.value]
  if (typeof activeElement?.scrollIntoView === 'function') {
    activeElement.scrollIntoView({ block: 'nearest' })
  }
}

function setAddItemActiveIndex(index) {
  addItemActiveIndex.value = index
  scrollActiveAddItemResultIntoView()
}

function isAddItemSearchResultSelectable(result) {
  return Boolean(result?.id) && !isPersonAlreadyOnList(result.id)
}

function setSelectableAddItemActiveIndex(result, index) {
  if (isAddItemSearchResultSelectable(result)) {
    setAddItemActiveIndex(index)
  }
}

function getNextSelectableAddItemIndex(direction) {
  const results = addItemSearchResults.value
  if (results.length === 0) {
    return -1
  }

  const startIndex = addItemActiveIndex.value < 0
    ? (direction > 0 ? -1 : results.length)
    : addItemActiveIndex.value

  for (let offset = 1; offset <= results.length; offset += 1) {
    const index = (startIndex + (direction * offset) + results.length) % results.length
    if (isAddItemSearchResultSelectable(results[index])) {
      return index
    }
  }

  return -1
}

function highlightNextAddItemResult() {
  const nextIndex = getNextSelectableAddItemIndex(1)
  if (nextIndex >= 0) {
    setAddItemActiveIndex(nextIndex)
  }
}

function highlightPreviousAddItemResult() {
  const previousIndex = getNextSelectableAddItemIndex(-1)
  if (previousIndex >= 0) {
    setAddItemActiveIndex(previousIndex)
  }
}

function handleAddItemEnter() {
  if (addItemActiveIndex.value < 0) {
    return
  }
  addSearchResult(addItemSearchResults.value[addItemActiveIndex.value])
}

async function addSearchResult(result) {
  if (!result?.id || isPersonAlreadyOnList(result.id)) {
    return
  }

  pendingAddedItems.value = [
    ...pendingAddedItems.value,
    {
      number: personList.value.items.length + 1,
      description: '',
      person: {
        id: result.id,
        person_id: result.person_id || result.id,
        name: result.name || '',
        url: result.url || '#',
        poster_url: result.poster_url || portraitPlaceholderImage,
        favourited: false,
      },
    },
  ]
  const nextNumber = personList.value.items.length + 1

  try {
    const response = await fetch(`/api/v1/people/lists/${listId.value}/`, {
      method: 'PATCH',
      headers: buildJsonAuthHeaders(),
      body: JSON.stringify({
        items: [
          ...personList.value.items.map((item) => ({
            id: item.id,
            description: item.description || '',
          })),
          { person_id: result.id, description: '', number: nextNumber },
        ],
      }),
    })

    if (!response.ok) {
      return
    }

    const data = await response.json()
    const fallbackNewItem = {
      id: `new-${result.id}`,
      number: nextNumber,
      description: '',
      added_at: null,
      updated_at: null,
      person: {
        id: result.id,
        person_id: result.person_id || result.id,
        name: result.name || '',
        url: result.url || '#',
        poster_url: result.poster_url || portraitPlaceholderImage,
        favourited: false,
      },
    }
    const nextItems = Array.isArray(data.items) ? data.items : [...personList.value.items, fallbackNewItem]

    personList.value = {
      ...personList.value,
      ...data,
      title: data.title ?? personList.value.title,
      description: data.description ?? personList.value.description,
      person_count: data.person_count ?? nextItems.length,
      items: nextItems,
    }

    const nextEditableItemDescriptions = { ...editableItemDescriptions.value }
    nextItems.forEach((item) => {
      if (!(item.id in nextEditableItemDescriptions)) {
        nextEditableItemDescriptions[item.id] = item.description || ''
      }
    })
    editableItemDescriptions.value = nextEditableItemDescriptions
    savedEditableItems.value = clonePersonListItems(personList.value.items)
    addItemNotice.value = `'${result.name || 'Item'}' added to the list as #${nextNumber}.`
  } finally {
    pendingAddedItems.value = pendingAddedItems.value.filter((item) => item?.person?.id !== result.id)
    resetAddItemSearch()
  }
}

function removeListItemFromEditing(itemId) {
  if (isListItemPendingRemoval(itemId)) {
    return
  }

  removedItemIds.value = [...removedItemIds.value, itemId]
}

function undoListItemRemoval(itemId) {
  removedItemIds.value = removedItemIds.value.filter((id) => id !== itemId)
}

function buildReorderedItemIds(items) {
  return (Array.isArray(items) ? items : []).map((item) => item.id)
}

function buildReorderItemNumbers(items) {
  return Object.fromEntries(
    (Array.isArray(items) ? items : []).map((item, index) => [
      item.id,
      Number.isFinite(Number.parseInt(item.number, 10)) ? Number.parseInt(item.number, 10) : index + 1,
    ]),
  )
}

function reorderItemNumber(item) {
  if (!item) {
    return 0
  }
  return reorderItemNumbers.value[item.id] ?? item.number ?? 0
}

function buildReorderPageLengths(itemCount) {
  const lengths = []
  let remaining = Math.max(0, itemCount)

  while (remaining > 0) {
    const nextLength = Math.min(pageSize, remaining)
    lengths.push(nextLength)
    remaining -= nextLength
  }

  return lengths.length > 0 ? lengths : [0]
}

function reorderPageStartIndex(pageNumber) {
  const boundedPageNumber = Math.max(1, pageNumber)
  return reorderPageLengths.value
    .slice(0, boundedPageNumber - 1)
    .reduce((sum, length) => sum + length, 0)
}

function reorderDisplayNumber(localIndex) {
  return reorderItemNumber(reorderPaginatedItems.value[localIndex])
}

function slugifyText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function buildFilmsomniacPath(username) {
  const normalizedUsername = String(username || '').trim()
  return normalizedUsername ? `/filmsomniacs/${encodeURIComponent(normalizedUsername)}` : '#'
}

function formatShortDate(value) {
  if (!value) {
    return ''
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed)
}

function countMatches(items, matcher) {
  return items.reduce((total, item) => (matcher(item) ? total + 1 : total), 0)
}

function compareStrings(left, right) {
  return String(left || '').localeCompare(String(right || ''), undefined, { sensitivity: 'base' })
}

function numericCompare(leftValue, rightValue, leftName, rightName) {
  const leftNumber = Number.parseInt(String(leftValue || 0), 10) || 0
  const rightNumber = Number.parseInt(String(rightValue || 0), 10) || 0
  if (leftNumber !== rightNumber) {
    return leftNumber - rightNumber
  }
  return compareStrings(leftName, rightName)
}

function truncatedPersonName(value) {
  const normalized = String(value || '').trim()

  if (normalized.length <= personNameDisplayLimit) {
    return normalized
  }

  const truncated = normalized.slice(0, personNameDisplayLimit).trim()
  const lastSpaceIndex = truncated.lastIndexOf(' ')

  if (lastSpaceIndex <= 0) {
    return `${truncated}...`
  }

  return `${truncated.slice(0, lastSpaceIndex).trim()}...`
}

function initializeFilters() {
  ignoreFilterPageReset.value = true
  void nextTick(() => {
    ignoreFilterPageReset.value = false
  })
  selectedActivity.value = 'all'
  sortMode.value = 'number-asc'
  currentViewMode.value = '1'
  viewModeBeforeReordering.value = '1'
  titleFilter.value = ''
  currentPage.value = normalizedPage(pageFromRoute())
  filtersModalOpen.value = false
}

function resetFiltersForNormalListMode() {
  selectedActivity.value = 'all'
  titleFilter.value = ''
  resetCurrentPage()
  filtersModalOpen.value = false
}

function personListDetailQuery(pageNumber, { sort = sortMode.value } = {}) {
  const params = new URLSearchParams()
  if (pageNumber > 1) {
    params.set('page', String(pageNumber))
  }
  if (isAuthenticated.value && selectedActivity.value !== 'all') {
    params.set('activity', selectedActivity.value)
  }
  if (sort !== 'number-asc') {
    params.set('sort', sort)
  }
  const queryString = params.toString()
  return queryString ? `?${queryString}` : ''
}

async function loadPersonListDetail(currentListId, {
  page = null,
  preserveFilters = false,
  preserveEditMode = false,
  sort = sortMode.value,
} = {}) {
  const confirmPageView = capturePageView()
  const shouldPreserveEditMode = preserveEditMode && listEditMode.value
  personListLoading.value = true
  personListLoaded.value = false
  personListError.value = ''
  if (!shouldPreserveEditMode) {
    listEditMode.value = false
  }
  reorderMode.value = false
  editableListTitle.value = ''
  editableListDescription.value = ''
  editableListPublic.value = true
  editableItemDescriptions.value = {}
  savedEditableItems.value = []
  resetPendingAddedItems()
  resetRemovedItemIds()
  addItemNotice.value = ''
  resetAddItemSearch()
  reorderedItemIds.value = []
  savedReorderItemIds.value = []
  reorderItemNumbers.value = {}
  savedReorderItemNumbers.value = {}
  reorderPageLengths.value = []
  savedReorderPageLengths.value = []
  clearReorderMoveNotice()
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()

  try {
    const requestedPage = page ?? pageFromRoute()
    const response = await fetch(`/api/v1/people/lists/${currentListId}/${personListDetailQuery(requestedPage, { sort })}`, {
      headers: buildAuthHeaders(),
    })

    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      personListError.value = 'This list could not be found.'
      personList.value = emptyPersonList()
      personListLoaded.value = true
      return
    }

    if (response.status === 404) {
      personListError.value = 'This list could not be found.'
      personList.value = emptyPersonList()
      personListLoaded.value = true
      return
    }

    if (!response.ok) {
      personListError.value = 'The list could not be loaded.'
      personList.value = emptyPersonList()
      personListLoaded.value = true
      return
    }

    const data = await response.json()
    if (data.redirect_url) {
      window.location.assign(data.redirect_url)
      return
    }
    confirmPageView()
    personList.value = {
      ...emptyPersonList(),
      ...data,
      items: Array.isArray(data.items) ? data.items : [],
      server_paginated: Object.prototype.hasOwnProperty.call(data, 'num_pages'),
    }
    savedEditableItems.value = clonePersonListItems(personList.value.items)
    if (shouldPreserveEditMode) {
      editableListTitle.value = personList.value.title || ''
      editableListDescription.value = personList.value.description || ''
      editableListPublic.value = Boolean(personList.value.public)
      editableItemDescriptions.value = buildEditableItemDescriptions(personList.value.items)
      listEditMode.value = true
    }
    if (preserveFilters) {
      currentPage.value = normalizedPage(Number.parseInt(String(data.page || requestedPage), 10) || requestedPage)
    } else {
      initializeFilters()
    }
    personListLoaded.value = true
  } catch {
    personListError.value = 'The list could not be loaded.'
    personList.value = emptyPersonList()
    personListLoaded.value = true
  } finally {
    personListLoading.value = false
  }
}

async function setSortMode(mode) {
  if (hasServerPagination.value && listId.value) {
    resetCurrentPage()
    await loadPersonListDetail(listId.value, { page: 1, preserveFilters: true, sort: mode })
  }
  sortMode.value = mode
}

function setViewMode(mode) {
  currentViewMode.value = mode
}

function toggleFilters() {
  filtersModalOpen.value = !filtersModalOpen.value
}

function applyFilter(groupKey, value) {
  if (groupKey === 'activity') {
    selectedActivity.value = value
  }

}

async function goToPage(pageNumber) {
  if (pageNumber < 1 || pageNumber > effectiveNumPages.value) {
    return
  }
  if (listEditMode.value && hasUnsavedChanges.value) {
    showUnsavedChangesNotice.value = true
    dismissSavedChangesNotice()
    return
  }
  clearReorderMoveNotice()
  await syncPageRoute(pageNumber, { replace: false })
  currentPage.value = pageNumber
  await scrollToListContentStart()
}

async function startListEditing() {
  if (!personListCanEdit.value) {
    return
  }

  if (listEditMode.value) {
    if (!hasUnsavedChanges.value) {
      editableListTitle.value = personList.value.title || ''
      editableListDescription.value = personList.value.description || ''
      editableListPublic.value = Boolean(personList.value.public)
      editableItemDescriptions.value = buildEditableItemDescriptions(personList.value.items)
      savedEditableItems.value = clonePersonListItems(personList.value.items)
      resetPendingAddedItems()
      resetRemovedItemIds()
      resetFiltersForNormalListMode()
      listEditMode.value = false
      showUnsavedChangesNotice.value = false
      addItemNotice.value = ''
      resetAddItemSearch()
      dismissSavedChangesNotice()
    } else {
      showUnsavedChangesNotice.value = true
      dismissSavedChangesNotice()
    }
    return
  }

  await loadAllReorderItems()
  editableListTitle.value = personList.value.title || ''
  editableListDescription.value = personList.value.description || ''
  editableListPublic.value = Boolean(personList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(personList.value.items)
  savedEditableItems.value = clonePersonListItems(personList.value.items)
  resetPendingAddedItems()
  resetRemovedItemIds()
  currentViewMode.value = '1'
  reorderMode.value = false
  listEditMode.value = true
  showUnsavedChangesNotice.value = false
  addItemNotice.value = ''
  resetAddItemSearch()
  dismissSavedChangesNotice()
}

function discardListEditing() {
  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  personList.value = {
    ...personList.value,
    person_count: savedEditableItems.value.length,
    items: clonePersonListItems(savedEditableItems.value),
  }
  editableListTitle.value = personList.value.title || ''
  editableListDescription.value = personList.value.description || ''
  editableListPublic.value = Boolean(personList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(personList.value.items)
  resetPendingAddedItems()
  resetRemovedItemIds()
  showUnsavedChangesNotice.value = false
  addItemNotice.value = ''
  resetAddItemSearch()
  dismissSavedChangesNotice()
}

async function saveListEditing() {
  if (!personListCanEdit.value || !listId.value) {
    return
  }

  if (hasPendingItemDeletions.value) {
    const shouldDelete = window.confirm('There are items to be deleted. Save changes and delete them?')
    if (!shouldDelete) {
      return
    }
  }

  const response = await fetch(`/api/v1/people/lists/${listId.value}/`, {
    method: 'PATCH',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({
      title: editableListTitle.value,
      description: editableListDescription.value,
      public: editableListPublic.value,
      removed_item_ids: removedItemIds.value,
      items: personList.value.items
        .filter((item) => !isListItemPendingRemoval(item.id))
        .map((item, index) => (
        item.id > 0
          ? {
              id: item.id,
              description: editableItemDescriptions.value[item.id] ?? '',
            }
          : {
              person_id: item.person.id,
              description: editableItemDescriptions.value[item.id] ?? '',
              number: index + 1,
            }
      )),
    }),
  })

  if (!response.ok) {
    return
  }

  const data = await response.json()
  const nextItems = Array.isArray(data.items)
    ? data.items
    : personList.value.items
      .filter((item) => !isListItemPendingRemoval(item.id))
      .map((item) => ({
        ...item,
        description: editableItemDescriptions.value[item.id] ?? '',
      }))
  personList.value = {
    ...personList.value,
    ...data,
    title: data.title ?? '',
    description: data.description ?? '',
    person_count: data.person_count ?? nextItems.length,
    items: nextItems,
  }
  editableListTitle.value = personList.value.title || ''
  editableListDescription.value = personList.value.description || ''
  editableListPublic.value = Boolean(personList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(personList.value.items)
  savedEditableItems.value = clonePersonListItems(personList.value.items)
  resetPendingAddedItems()
  resetRemovedItemIds()
  showUnsavedChangesNotice.value = false
  addItemNotice.value = ''
  resetAddItemSearch()
  showSavedChangesNotice.value = true
  scheduleSavedChangesNoticeDismiss()
}

async function deletePersonList() {
  if (!personListCanEdit.value || !listId.value || listDeletePending.value) {
    return
  }

  const shouldDelete = window.confirm(deleteListConfirmationMessage)
  if (!shouldDelete) {
    return
  }

  listDeletePending.value = true
  try {
    const response = await fetch(`/api/v1/people/lists/${listId.value}/`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })

    if (response.ok) {
      storeListDeletedNotice('People', personList.value.title)
      await router.push('/home/lists')
    }
  } finally {
    listDeletePending.value = false
  }
}

function storeListDeletedNotice(listMediumType, listTitle) {
  try {
    window.sessionStorage.setItem(
      listDeletedNoticeStorageKey,
      JSON.stringify({
        message: `${listMediumType} list '${String(listTitle || '').trim() || 'Untitled'}' was deleted.`,
      }),
    )
  } catch {
    // The redirect still works if browser storage is unavailable.
  }
}

const personListCanEdit = computed(() => Boolean(
  personList.value.can_edit ?? personList.value.is_owner,
))

const { resetNumbersPending, resetNumbersNotice, resetNumbers } = useListNumberReset(
  personList,
  buildAuthHeaders,
  () => loadPersonListDetail(listId.value, { page: currentPage.value, preserveFilters: true }),
)

async function startReordering() {
  if (!personListCanEdit.value) {
    return
  }

  titleFilter.value = ''
  await loadAllReorderItems()
  viewModeBeforeReordering.value = currentViewMode.value
  reorderedItemIds.value = buildReorderedItemIds(sortedItems.value)
  savedReorderItemIds.value = buildReorderedItemIds(sortedItems.value)
  reorderItemNumbers.value = buildReorderItemNumbers(sortedItems.value)
  savedReorderItemNumbers.value = { ...reorderItemNumbers.value }
  reorderPageLengths.value = buildReorderPageLengths(sortedItems.value.length)
  savedReorderPageLengths.value = [...reorderPageLengths.value]
  clearReorderMoveNotice()
  resetCurrentPage()
  currentViewMode.value = '4'
  listEditMode.value = false
  reorderMode.value = true
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()
}

async function loadAllReorderItems() {
  if (!hasServerPagination.value || numPages.value <= 1 || !listId.value) {
    return
  }

  const pageCount = numPages.value
  const pageItems = new Map()

  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    if (pageNumber === currentPage.value) {
      pageItems.set(pageNumber, personList.value.items)
      continue
    }

    const response = await fetch(`/api/v1/people/lists/${listId.value}/${personListDetailQuery(pageNumber)}`, {
      headers: buildAuthHeaders(),
    })

    if (!response.ok) {
      return
    }

    const data = await response.json()
    pageItems.set(pageNumber, Array.isArray(data.items) ? data.items : [])
  }

  const combinedItems = []
  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    combinedItems.push(...(pageItems.get(pageNumber) || []))
  }

  personList.value = {
    ...personList.value,
    person_count: combinedItems.length,
    items: combinedItems,
    page: 1,
    num_pages: Math.max(1, Math.ceil(combinedItems.length / pageSize)),
    server_paginated: false,
  }
}

function resetReorderChanges() {
  reorderedItemIds.value = [...savedReorderItemIds.value]
  reorderItemNumbers.value = { ...savedReorderItemNumbers.value }
  reorderPageLengths.value = [...savedReorderPageLengths.value]
  clearReorderMoveNotice()
}

function finishReordering() {
  if (!reorderMode.value) {
    return
  }

  if (!hasReorderChanges.value) {
    currentViewMode.value = viewModeBeforeReordering.value || '1'
    resetCurrentPage()
    reorderMode.value = false
    showUnsavedChangesNotice.value = false
    dismissSavedChangesNotice()
    return
  }

  showUnsavedChangesNotice.value = true

  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  resetReorderChanges()
  currentViewMode.value = viewModeBeforeReordering.value || '1'
  resetCurrentPage()
  reorderMode.value = false
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()
}

function discardReorderChanges() {
  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  resetReorderChanges()
}

async function saveReordering() {
  if (!personListCanEdit.value || !listId.value) {
    return
  }

  const reorderedItems = reorderOrderedItems.value
    .map((item) => ({
      id: item.id,
      description: item.description || '',
      number: reorderItemNumber(item),
    }))
    .filter((item) => item.number !== savedReorderItemNumbers.value[item.id])

  const response = await fetch(`/api/v1/people/lists/${listId.value}/`, {
    method: 'PATCH',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({
      title: personList.value.title || '',
      description: personList.value.description || '',
      removed_item_ids: [],
      items: reorderedItems,
    }),
  })

  if (!response.ok) {
    return
  }

  const data = await response.json()
  const reorderedItemsById = new Map(reorderedItems.map((item) => [item.id, item]))
  const nextItems = reorderOrderedItems.value.map((item) => ({
    ...item,
    description: reorderedItemsById.get(item.id)?.description ?? item.description ?? '',
    number: reorderItemNumber(item),
  }))

  personList.value = {
    ...personList.value,
    ...data,
    title: data.title ?? personList.value.title ?? '',
    description: data.description ?? personList.value.description ?? '',
    items: nextItems,
  }
  reorderedItemIds.value = buildReorderedItemIds(personList.value.items)
  savedReorderItemIds.value = [...reorderedItemIds.value]
  reorderItemNumbers.value = buildReorderItemNumbers(personList.value.items)
  savedReorderItemNumbers.value = { ...reorderItemNumbers.value }
  reorderPageLengths.value = buildReorderPageLengths(personList.value.items.length)
  savedReorderPageLengths.value = [...reorderPageLengths.value]
  clearReorderMoveNotice()
}

function reorderItemIndex(itemId) {
  return reorderOrderedItems.value.findIndex((item) => item.id === itemId)
}

function isFirstReorderItem(itemId) {
  return reorderItemIndex(itemId) === 0
}

function isLastReorderItem(itemId) {
  const itemIndex = reorderItemIndex(itemId)
  return itemIndex === reorderOrderedItems.value.length - 1
}

function clearReorderMoveNotice() {
  clearReorderMoveNoticeTimeout()
  reorderMoveNotice.value = { text: '', position: '' }
}

function showReorderMoveNotice(text, position) {
  clearReorderMoveNoticeTimeout()
  reorderMoveNotice.value = { text, position }
  reorderMoveNoticeTimeoutId = window.setTimeout(() => {
    reorderMoveNotice.value = { text: '', position: '' }
    reorderMoveNoticeTimeoutId = null
  }, 5000)
}

function personReorderItemTitle(item) {
  return item?.person?.name || 'List item'
}

function moveReorderItem(itemId, direction) {
  if (!reorderMode.value || !direction) {
    return
  }

  const nextIds = [...reorderedItemIds.value]
  const currentIndex = nextIds.indexOf(itemId)
  const targetIndex = currentIndex + direction

  if (currentIndex === -1 || targetIndex < 0 || targetIndex >= nextIds.length) {
    return
  }

  const currentPageStartIndex = reorderPageStartIndex(currentPage.value)
  const currentPageLength = reorderPageLengths.value[currentPage.value - 1] || pageSize
  const currentPageEndIndex = currentPageStartIndex + currentPageLength - 1
  const movedItem = reorderOrderedItems.value[currentIndex]
  const movedTitle = personReorderItemTitle(movedItem)

  const targetId = nextIds[targetIndex]
  const targetItem = reorderOrderedItems.value[targetIndex]
  nextIds[targetIndex] = itemId
  nextIds[currentIndex] = targetId

  reorderedItemIds.value = nextIds
  reorderItemNumbers.value = {
    ...reorderItemNumbers.value,
    [itemId]: reorderItemNumber(targetItem),
    [targetId]: reorderItemNumber(movedItem),
  }

  if (direction > 0 && currentIndex === currentPageEndIndex) {
    showReorderMoveNotice(`${movedTitle} was moved to next page to place #${targetIndex + 1}.`, 'below')
  } else if (direction < 0 && currentIndex === currentPageStartIndex) {
    showReorderMoveNotice(`${movedTitle} was moved to previous page to place #${targetIndex + 1}.`, 'above')
  } else {
    clearReorderMoveNotice()
  }
}

function listItemFavouriteButtonText(item) {
  return item?.person?.favourited ? 'Favourited' : 'Add to favourites'
}

function listItemFavouriteButtonShortText(item) {
  return item?.person?.favourited ? 'Favourited' : 'Favourite'
}

async function toggleListItemFavourite(item) {
  const personId = item?.person?.person_id
  if (!personId || !isAuthenticated.value || personFavouritePendingById.value[personId]) {
    return
  }

  personFavouritePendingById.value[personId] = true
  try {
    const response = await fetch(`/api/v1/people/${personId}/favourite`, {
      method: 'POST',
      headers: buildJsonAuthHeaders(),
      body: JSON.stringify({ source: 'L' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    item.person.favourited = Boolean(data.favourited)
  } catch {
    // Leave the local favourite state unchanged on request failure.
  } finally {
    personFavouritePendingById.value[personId] = false
  }
}

useMetaDescription(() => buildMetaDescription(
  'person-list',
  personListLoaded.value && !personListError.value ? { title: personList.value.title } : {},
))
</script>
